import { useCallback, useEffect, useMemo, useState } from 'react';
import { panelStorageKey } from '@/components/common/useCollapsiblePanelState';

const NAV_GROUP_SCOPE = 'nav-group';

function readGroupExpanded(groupId: string, fallback: boolean): boolean {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(panelStorageKey(NAV_GROUP_SCOPE, groupId));
    if (raw === 'true') return true;
    if (raw === 'false') return false;
  } catch {
    /* ignore */
  }
  return fallback;
}

function writeGroupExpanded(groupId: string, expanded: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(panelStorageKey(NAV_GROUP_SCOPE, groupId), String(expanded));
  } catch {
    /* ignore */
  }
}

/** Persisted expand/collapse state for navigation groups (desktop + mobile). */
export function useNavGroupAccordion(activeGroupId: string | null, groupIds: string[]) {
  const groupKey = useMemo(() => groupIds.join('|'), [groupIds]);

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const id of groupIds) {
      initial[id] = readGroupExpanded(id, id === activeGroupId);
    }
    return initial;
  });

  useEffect(() => {
    setExpandedGroups((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const id of groupIds) {
        if (!(id in next)) {
          next[id] = readGroupExpanded(id, id === activeGroupId);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [groupKey, groupIds, activeGroupId]);

  useEffect(() => {
    if (!activeGroupId) return;
    setExpandedGroups((prev) => {
      if (prev[activeGroupId]) return prev;
      writeGroupExpanded(activeGroupId, true);
      return { ...prev, [activeGroupId]: true };
    });
  }, [activeGroupId]);

  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      const value = !prev[groupId];
      writeGroupExpanded(groupId, value);
      return { ...prev, [groupId]: value };
    });
  }, []);

  const isExpanded = useCallback(
    (groupId: string) => Boolean(expandedGroups[groupId]),
    [expandedGroups],
  );

  return { isExpanded, toggleGroup };
}
